import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { useTarget } from "lib/@hooks/use-target"
import { Alert, Box, Button, Card, CardActions, CardContent, CardHeader, Stack, ToggleButton } from "@mui/material"
import { BoundToggleButtonGroup, StandardAutocomplete } from "lib/@components/binding/bound-components"
import Iconify from "minimals-template/components/Iconify"
import { ListItemBox } from "lib/@components/ListItemBox"
import noop from "lib/noop"
import { toDataCy } from "lib/to-data-cy"
import { LEGACY_LAWS } from "routes/asset-register/plugins/asset-mapping/constants"

export function LegacyInnerSettings() {
    const { inDialog, cancel = noop, ok = noop, saveSettings = noop, setHideSettings = noop } = useBoundContext()
    const target = useTarget()
    const [english, setEnglish] = useBoundValue("english", false)
    const [welsh, setWelsh] = useBoundValue("welsh", false)
    const [scottish, setScottish] = useBoundValue("scottish", false)
    const [healthcare, setHealthcare] = useBoundValue("healthcare", false)
    const [mothballing, setMothballing] = useBoundValue("mothballing", false)
    const [reactivation, setReactivation] = useBoundValue("reactivation", false)
    const [northernIreland, setNorthernIreland] = useBoundValue("northernIreland", false)
    const [originalTypes, setOriginalTypes] = useBoundValue("originalTypes", false)
    const legislation = [
        healthcare && "Healthcare",
        english && "English Legislation",
        welsh && "Welsh Legislation",
        scottish && "Scottish Legislation",
        northernIreland && "Northern Ireland Legislation",
        reactivation && "Reactivation",
        mothballing && "Mothballing",
        originalTypes && "Use Unrefined Types",
    ].filter(Boolean)

    return (
        <Box data-cy="mapping-settings">
            <Card>
                <CardHeader title={"Mapping Settings"} />
                <CardContent>
                    <Stack spacing={2}>
                        <Alert severity="info">
                            These settings let you configure how schedules are mapped to assets: by selecting the
                            relevant legislation, prioritising facility types (e.g. Healthcare), choosing not to use
                            AI-derived asset types if required, and setting the confidence threshold for automatic
                            mapping.
                        </Alert>
                        <StandardAutocomplete
                            options={Object.values(LEGACY_LAWS)}
                            onChange={setLegislation}
                            value={legislation}
                            data-cy="mapping-rule-legislation"
                            multiple
                            label="Settings"
                            renderOption={(props, option) => (
                                <li {...props} data-cy={`${toDataCy("mapping-setting-legislation", option)}`}>
                                    {option}
                                </li>
                            )}
                        />
                        <BoundToggleButtonGroup
                            size="small"
                            fullWidth
                            field="mappingRule"
                            defaultValue="strict"
                            data-cy="mapping-rule"
                        >
                            <ToggleButton value="strict" data-cy="mapping-rule-strict">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Iconify icon="mdi:shield" />
                                    <Box>Strict Auto-Selection</Box>
                                </Stack>
                            </ToggleButton>
                            <ToggleButton value="moderate" data-cy="mapping-rule-moderate">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Iconify icon="mdi:justice" />
                                    <Box>Moderate Auto-Selection</Box>
                                </Stack>
                            </ToggleButton>
                            <ToggleButton value="relaxed" data-cy="mapping-rule-relaxed">
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Iconify icon="ph:list-magnifying-glass" />
                                    <Box>Relaxed Auto-Selection</Box>
                                </Stack>
                            </ToggleButton>
                        </BoundToggleButtonGroup>
                    </Stack>
                </CardContent>
                <CardActions>
                    <ListItemBox spacing={1}>
                        <Box flex={1} />
                        {!!inDialog && (
                            <Button onClick={cancel} color="secondary">
                                Cancel
                            </Button>
                        )}
                        <Button
                            onClick={configureSettings}
                            variant="contained"
                            data-cy="asset-mapping-schedule-mapping-apply-settings-button"
                        >
                            Apply Settings
                        </Button>
                    </ListItemBox>
                </CardActions>
            </Card>
        </Box>
    )

    async function configureSettings() {
        // FIQ-1554: Apply can be clicked inside the EditRecord's 1s save debounce. Stamp
        // the flag on the live edit target so the pending debounced write-back cannot
        // revert it, and hand the current edits straight to save so they are never lost
        // to that race (previously the fallback saved the register's original options).
        target.hasConfiguredSettings = true
        await saveSettings(target)
        setHideSettings(true)
        ok()
    }

    function setLegislation(_, value) {
        setEnglish(value.includes("English Legislation"))
        setWelsh(value.includes("Welsh Legislation"))
        setScottish(value.includes("Scottish Legislation"))
        setNorthernIreland(value.includes("Northern Ireland Legislation"))
        setHealthcare(value.includes("Healthcare"))
        setMothballing(value.includes("Mothballing"))
        setReactivation(value.includes("Reactivation"))
        setOriginalTypes(value.includes("Use Unrefined Types"))
    }
}
